import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase'; // Ensure this points to your firebase init file
import { useToast } from '../../contexts/ToastContext';

export default function NotificationBell({ user, navigateTo, dropdownAlign = 'right' }) {
    const [notifications, setNotifications] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const toast = useToast(); 
    const initialLoadRef = useRef(true); 

    useEffect(() => {
        // Only attempt to fetch notifications if there is a real, non-anonymous user.
        if (!user || user.isAnonymous) {
            setNotifications([]); // Clear any old notifications
            return;
        }

        // Reset initial load ref when setting up a new listener to prevent toast spam
        initialLoadRef.current = true;

        const q = query(collection(db, 'notifications'), where('userId', '==', user.uid));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedNotifications = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            // Sort notifications by creation date, descending
            fetchedNotifications.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setNotifications(fetchedNotifications);

            // Show toast for new incoming notifications
            if (!initialLoadRef.current) {
                snapshot.docChanges().forEach((change) => {
                    if (change.type === 'added') {
                        const notif = change.doc.data();
                        // Prevent toast avalanche on login by checking if notification is actually recent (within 10 seconds)
                        const isRecent = !notif.createdAt || (Date.now() / 1000 - notif.createdAt.seconds < 10);
                        if (!notif.isRead && isRecent) {
                            // Support for both custom addToast and direct success calls depending on your context structure
                            if (toast.addToast) {
                                const type = notif.message.toLowerCase().includes('message') ? 'info' : 'success';
                                toast.addToast(notif.message, type);
                            } else if (toast.success) {
                                toast.success(notif.message);
                            }
                        }
                    }
                });
            }
            initialLoadRef.current = false;
        },
        (error) => {
            // Ignore permission-denied errors that happen briefly during logout
            if (error.code !== 'permission-denied') {
                console.error("Error fetching notifications:", error);
            }
        });

        return () => unsubscribe();
    }, [user?.uid, user?.isAnonymous]); // Removed db from dep array since it's imported globally now

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleNotificationClick = async (notification) => {
        if (!notification.isRead) {
            await updateDoc(doc(db, 'notifications', notification.id), { isRead: true });
        }
        if (notification.destination) {
            navigateTo(notification.destination.page, notification.destination.data);
        } else {
            navigateTo('dashboard');
        }
        setIsOpen(false);
    };

    const handleMarkAllRead = async (e) => {
        e.stopPropagation();
        const unreadDocs = notifications.filter(n => !n.isRead);
        if (unreadDocs.length === 0) return;

        // Execute updates in parallel
        await Promise.all(
            unreadDocs.map(n => updateDoc(doc(db, 'notifications', n.id), { isRead: true }))
        );
    };

    const unreadCount = notifications.filter(n => !n.isRead).length;

    return (
        <div className="relative" ref={dropdownRef}>
            <button onClick={() => setIsOpen(!isOpen)} className="relative p-2 text-gray-600 hover:text-pink-500 transition-colors rounded-full focus:outline-none">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white transform translate-x-1/4 -translate-y-1/4">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>
            
            {isOpen && (
                <div className={`absolute ${dropdownAlign === 'left' ? 'left-0' : 'right-0'} mt-2 w-80 bg-white rounded-xl shadow-2xl overflow-hidden z-50 border border-gray-100 ring-1 ring-black ring-opacity-5 animate-fade-in`}>
                    <div className="py-2">
                        <div className="px-4 py-3 font-bold text-gray-800 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                            <span>Notifications</span>
                            {unreadCount > 0 && (
                                <span 
                                    onClick={handleMarkAllRead} 
                                    className="text-xs font-bold text-pink-600 cursor-pointer hover:text-pink-800 transition-colors uppercase tracking-wider"
                                >
                                    Mark all read
                                </span>
                            )}
                        </div>
                        <div className="max-h-96 overflow-y-auto custom-scrollbar">
                            {notifications.length > 0 ? notifications.map(n => {
                                // Simple logic to pick an icon based on message content
                                const isMessage = n.message.toLowerCase().includes('message');
                                const isBooking = n.message.toLowerCase().includes('booking') || n.message.toLowerCase().includes('order');
                                
                                return (
                                    <div key={n.id} onClick={() => handleNotificationClick(n)} className={`px-4 py-3 cursor-pointer hover:bg-gray-50 flex gap-3 transition-colors ${!n.isRead ? 'bg-pink-50/40 border-l-2 border-pink-500' : 'border-l-2 border-transparent'}`}>
                                        <div className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${!n.isRead ? 'bg-pink-500' : 'bg-transparent'}`}></div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start gap-2 mb-1">
                                                <span className={`p-1.5 rounded-full text-xs flex-shrink-0 ${isMessage ? 'bg-blue-100 text-blue-600' : isBooking ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
                                                    {isMessage ? '💬' : isBooking ? '📅' : '🔔'}
                                                </span>
                                                <p className={`text-sm leading-tight ${!n.isRead ? 'text-gray-900 font-bold' : 'text-gray-600 font-medium'}`}>{n.message}</p>
                                            </div>
                                            <p className="text-xs text-gray-400 pl-8 font-medium">
                                                {n.createdAt?.seconds ? new Date(n.createdAt.seconds * 1000).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Just now'}
                                            </p>
                                        </div>
                                    </div>
                                );
                            }) : (
                                <div className="px-4 py-12 text-center flex flex-col items-center justify-center">
                                    <span className="text-4xl mb-3 opacity-50">📭</span>
                                    <p className="text-gray-500 text-sm font-medium">No new notifications.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}